/**
 * Constantes para o componente Result Multi Column
 */

import { AVATAR_FIELDS } from '../shared/avatar/avatar.constants';

/**
 * Campos que devem ser ocultados na exibição
 */
export const HIDDEN_FIELDS = ['genero', 'gender', 'sexo', 'ativo', 'activated', 'active', 'tipo', 'type', 'entry_type', 'tipo_entrada'];

/**
 * Campos de data
 */
export const DATE_FIELDS = ['data', 'date', 'data_transacao', 'data_entrada', 'created_at', 'updated_at', 'nascimento', 'birthday'];

/**
 * Campos que devem ser capitalizados
 */
export const CAPITALIZE_FIELDS = ['forma_pagamento', 'payment_method', 'metodo', 'bandeira', 'brand', 'status'];

/**
 * Mapeamento de labels para campos comuns
 */
export const FIELD_LABELS: { [key: string]: string } = {
    'nome': 'Nome',
    'total': 'Total',
    'valor': 'Valor',
    'homens': 'Homens',
    'mulheres': 'Mulheres',
    'ativos': 'Ativos',
    'inativos': 'Inativos',
    'membros': 'Membros',
    'congregados': 'Congregados',
    'quantidade': 'Quantidade',
    'percentual': 'Percentual',
    'media': 'Média',
    'data': 'Data',
    'forma_pagamento': 'Forma Pagamento'
};

/**
 * Mapeamento de tradução de valores
 */
export const VALUE_TRANSLATIONS: { [key: string]: string } = {
    'tithe': 'Dízimo',
    'offer': 'Oferta',
    'designated': 'Designada',
    'campaign': 'Campanha',
    'vote': 'Voto',
    'pix': 'Pix',
    'cash': 'Dinheiro',
    'credit': 'Crédito',
    'debit': 'Débito',
    'transfer': 'Transferência'
};

/**
 * Verifica se o campo é de avatar
 */
export function isAvatarField(key: string): boolean {
    return AVATAR_FIELDS.includes(key.toLowerCase());
}

/**
 * Verifica se o campo deve ser ocultado
 */
export function shouldHideField(key: string): boolean {
    return HIDDEN_FIELDS.includes(key.toLowerCase());
}

/**
 * Obtém o label para um campo
 */
export function getFieldLabel(key: string): string {
    const lowerKey = key.toLowerCase();

    if (FIELD_LABELS[lowerKey]) {
        return FIELD_LABELS[lowerKey];
    }

    // Converte snake_case para Title Case
    return key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}

/**
 * Formata valor genérico
 */
export function formatValue(value: any, key: string): string {
    if (value === null || value === undefined) return '-';

    const lowerKey = key.toLowerCase();

    // Valores monetários
    if (['valor', 'saldo', 'total', 'amount', 'limite', 'gasto', 'gastos'].includes(lowerKey)) {
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (!isNaN(numValue)) {
            return numValue.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            });
        }
    }

    // Campos de data
    if (DATE_FIELDS.includes(lowerKey)) {
        return formatDate(value);
    }

    // Campos que devem ser capitalizados/traduzidos
    if (CAPITALIZE_FIELDS.includes(lowerKey)) {
        return translateAndCapitalize(value);
    }

    // Valores numéricos
    if (typeof value === 'number') {
        return value.toLocaleString('pt-BR');
    }

    return String(value);
}

/**
 * Formata data para DD/MM/YYYY
 */
export function formatDate(value: any): string {
    if (!value) return '-';

    const strValue = String(value);

    // Se já está formatado como DD/MM/YYYY
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(strValue)) {
        return strValue;
    }

    // Se está no formato ISO (2025-12-05T03:00:00.000Z)
    if (strValue.includes('T') || strValue.includes('-')) {
        try {
            const date = new Date(strValue);
            if (!isNaN(date.getTime())) {
                return date.toLocaleDateString('pt-BR');
            }
        } catch {
            // ignore
        }
    }

    // Se está no formato DDMMYYYY
    if (/^\d{8}$/.test(strValue)) {
        return `${strValue.slice(0, 2)}/${strValue.slice(2, 4)}/${strValue.slice(4, 8)}`;
    }

    return strValue;
}

/**
 * Traduz e capitaliza valor
 */
export function translateAndCapitalize(value: any): string {
    if (!value) return '-';

    const strValue = String(value).toLowerCase();

    // Verifica se tem tradução
    if (VALUE_TRANSLATIONS[strValue]) {
        return VALUE_TRANSLATIONS[strValue];
    }

    // Capitaliza primeira letra
    return strValue.charAt(0).toUpperCase() + strValue.slice(1);
}
